import React, { useEffect, useState } from 'react';
import { PlaidLinkStableEvent, usePlaidLink } from 'react-plaid-link';
import { ButtonBase } from '@americanexpress/dls-react';
import { usePlaidReconnectInstitution, usePlaidResetInstitutionLogin } from 'src/hooks/queries/plaid';
import { invalidateAllAccountRelatedQueries } from 'src/hooks/queries/accounts';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useLocation } from 'react-router-dom';
import { PlaidReconnectInstitutionViewModel } from 'src/types/view-models/atoms/plaid-reconnect-institution.view.model';
import PlaidService, { PlaidMetaData } from 'src/services/plaid.service';
import Selectors from 'src/utils/selectors';

export default function PlaidReconnectInstitution({
    id,
    className,
    children
}: PlaidReconnectInstitutionViewModel): JSX.Element {
    const { mutateAsync: reconnectInstitution } = usePlaidReconnectInstitution();
    const { mutateAsync: resetInstitutionLogin } = usePlaidResetInstitutionLogin();
    const [plaidReconnectToken, setPlaidReconnectToken] = useState<string>(null);
    const [openPlaidModal, setOpenPlaidModal] = useState<boolean>(null);
    const queryClient: QueryClient = useQueryClient();
    const { pathname } = useLocation();

    const { open, ready } = usePlaidLink({
        token: plaidReconnectToken,
        onSuccess: () => {
            const asyncHelper = async (): Promise<void> => {
                await resetInstitutionLogin(id);
                await invalidateAllAccountRelatedQueries(queryClient);
            };
            void asyncHelper();
        },
        onEvent: (eventName, metadata) => {
            if (eventName === PlaidLinkStableEvent.EXIT || eventName === PlaidLinkStableEvent.ERROR)
                void PlaidService.postUserLog(eventName, metadata as PlaidMetaData);
        }
    });

    useEffect(() => {
        if (openPlaidModal && ready) {
            void open();
            setOpenPlaidModal(false);
        }
    }, [openPlaidModal, ready]);

    return (
        <ButtonBase
            className={className}
            data-cy={Selectors.General.PLAID_RECONNECT_INSTITUTION}
            onClick={async () => {
                trackClick(pathname, adobeTags.Error.Reconnect);
                await reconnectInstitution(id, {
                    onSuccess: (data) => {
                        setPlaidReconnectToken(data);
                        setOpenPlaidModal(true);
                    }
                });
            }}
        >
            {children}
        </ButtonBase>
    );
}
